import { useEffect, useState } from 'react';
import { Skeleton } from 'antd';
import moment from 'moment';

import { DataForm, FieldProps, FieldType } from 'components';
import ScannerModal from 'components/DataForm/components/ScannerModal';
import { Drawers } from 'app/types/drawer';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useLazyGetLoggerOverviewQuery, useLazyGetLoggerProjectToolsQuery, useUpdateLoggerProjectMutation } from 'app/services/loggers';
import { actions as loggerAccess, selectors as loggerAccessSelectors } from 'app/services/loggers/slice';
import { ToolOption } from 'app/services/loggers/types';
import { formatMomentToDate, formats } from 'utils/datetime';
import { parseQrValue } from 'utils/qr/parseQrValue';

type ToolChoice = { label: string, value: number, qrCode: string };

const toChoices = (tools: ToolOption[]): ToolChoice[] => tools.map((tool) => ({ label: tool.Label, value: tool.Id, qrCode: tool.QrCode }));

const EditLoggerProject = ({ barcode }: { barcode: string }) => {
  const dispatch = useAppDispatch();
  const access = useAppSelector(loggerAccessSelectors.getLoggerAccess(barcode));

  const [ updateProject, meta ] = useUpdateLoggerProjectMutation();
  const [ getOverview ] = useLazyGetLoggerOverviewQuery();
  const [ getTools, { data: tools, isLoading: isLoadingTools } ] = useLazyGetLoggerProjectToolsQuery();

  const [ toolChoices, setToolChoices ] = useState<ToolChoice[]>([]);

  useEffect(() => {
    if (access) getTools({ barcode, code: access.code });
  }, []);

  useEffect(() => {
    if (tools) setToolChoices(toChoices(tools));
  }, [ tools ]);

  const project = access?.overview.Project;

  if (!access || !project || isLoadingTools) return <Skeleton active />;

  // Local filtering of the loaded tools, like the tools field of the logged-in workplace form.
  const searchTools = (value: string) => {
    const all = toChoices(tools ?? []);
    setToolChoices(value ? all.filter((tool) => tool.label.toLowerCase().includes(value.toLowerCase())) : all);
  };

  // A scanned tool QR (a URL these days, so the barcode is parsed out of it): return the matching choice
  // and ScannerModal adds it to the selection, the same way the logged-in tool fields work.
  const scannerSearchTools = (scanned: string) => {
    const { barcodeValue } = parseQrValue(scanned);
    const match = toChoices(tools ?? []).find((choice) => choice.qrCode === barcodeValue);
    return match ? [ match ] : [];
  };

  const fields: FieldProps[] = [
    {
      name: 'Start',
      required: true,
      type: FieldType.DATE,
      datePicker: {
        dateFormat: formats.DD_MM_YYYY_HH_mm,
        showTime: true
      }
    },
    { name: 'WorkPlace', required: true },
    { name: 'WorkName', required: true },
    { name: 'WorkerName', required: true },
    {
      name: 'ToolIds',
      type: FieldType.SELECT_SEARCH,
      searchProps: {
        options: toolChoices,
        multiple: true,
        searchCallback: searchTools,
        setDataCallback: undefined,
        localFilter: true,
      },
      allowEmptySearch: true,
      addOn: (
        <ScannerModal
          searchCallBack={scannerSearchTools}
          transformValue={(scanned) => parseQrValue(scanned).barcodeValue}
          local
          multiple
        />
      ),
    },
    { name: 'HighLimit', required: true, type: FieldType.NUMBER, numberProps: { min: -32768, max: 32767, precision: 0, controls: false } },
    { name: 'LowLimit', required: true, type: FieldType.NUMBER, numberProps: { min: -32768, max: 32767, precision: 0, controls: false } },
    { name: 'Frequency', required: true, type: FieldType.NUMBER, numberProps: { min: 1, max: 32767, precision: 0, controls: false } }
  ];

  const handleSubmit = async (values: any) => {
    try {
      await updateProject({
        barcode,
        code: access.code,
        project: {
          Id: project.Id,
          Start: formatMomentToDate(values.Start),
          WorkPlace: values.WorkPlace,
          WorkName: values.WorkName,
          WorkerName: values.WorkerName,
          LowLimit: values.LowLimit,
          HighLimit: values.HighLimit,
          Frequency: values.Frequency,
          ToolIds: values.ToolIds ?? []
        }
      }).unwrap();

      const overview = await getOverview({ barcode, code: access.code }).unwrap();
      dispatch(loggerAccess.updateOverview(overview));
    } catch {
      // Reported through the global error toast.
    }
  };

  return <DataForm
    id={Drawers.EDIT_LOGGER_PROJECT}
    entity="project"
    onSubmit={handleSubmit}
    fields={fields}
    meta={meta}
    initialValues={{
      Start: moment(project.Start),
      WorkPlace: project.WorkPlace,
      WorkName: project.WorkName,
      WorkerName: project.WorkerName,
      LowLimit: project.LowLimit,
      HighLimit: project.HighLimit,
      Frequency: project.Frequency,
      ToolIds: access.overview.Tools.map((tool) => tool.ToolId)
    }}
    noSuggestions
  />;
};

export default EditLoggerProject;
